import { Component, HostListener, OnInit, OnDestroy, computed } from '@angular/core';
import { Services } from 'src/app/services/api.service';
import { EventsService } from 'src/app/services/getEvents';
import { environment } from 'src/environments/environment';
import { MapService } from '../membership/map/map.service';
import { Subscription } from 'rxjs';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { MembershipService } from '../membership/membership.service';
import { Digit } from 'src/app/common/interfaces';
import { CommonModule } from '@angular/common';
import { HeroEventComponent } from 'src/app/components/hero-event/hero-event.component';
import { EventsListComponent } from 'src/app/components/events-list/events-list.component';
import { MapComponent } from '../membership/map/map.component';
import { ModalNames } from 'src/app/common/modal.config';
import { ModalService } from 'src/app/services/modal.service';
import { TranslateModule } from '@ngx-translate/core';
import { SeasonTicketComponent } from '../season-ticket/season-ticket.component';
import { PromoVideoComponent } from 'src/app/components/promo/promo.component';
import { OrderService } from 'src/app/services/getOrder.service';
import {NewEventsListComponent} from "../../components/new-events-list/new-events-list.component";

@Component({
  selector: 'app-homepage',
  templateUrl: `./${environment.pageFolder}/homepage.${environment.pageFolder}.component.html`,
  styleUrls: [`./${environment.pageFolder}/homepage.${environment.pageFolder}.component.sass`],
  animations: [
    trigger('mapState', [
      state('collapsed', style({
        transform: 'scale(0)',
        opacity: 0,
      })),
      state('expanded', style({
        transform: 'scale(1)',
        opacity: 1,
      })),
      transition('collapsed <=> expanded', [
        animate('300ms ease-in-out')
      ])
    ])
  ],
  standalone: true,
  imports: [CommonModule, HeroEventComponent, EventsListComponent, MapComponent, TranslateModule, SeasonTicketComponent, PromoVideoComponent, NewEventsListComponent]
})
export class HomepageComponent implements OnInit, OnDestroy {
  allEvents: any;
  redirectUrl = environment.redirectUrl;
  membershipCountFormatted: Digit[] = [];
  membershipDataLoaded = false;
  isMapOpen: boolean = true;
  areEventsLoaded: boolean = false;
  selectedEvent: any = null;
  parent: string = "";
  page: string = environment.page;
  private subscriptions = new Subscription();
  private readonly fixedDigitCount: number = 5;
  public readonly modalNamesEnum = ModalNames;
  readonly isHomepageModalOpen = computed(() => this.modalService.modalsState().get(ModalNames.HomepageModal) ?? false);
  private orderSub!: Subscription;
  public currentOrderEventId: any | null = null;

  constructor(
    public Services: Services,
    private eventsService: EventsService,
    private mapService: MapService,
    private membershipService: MembershipService,
    public modalService: ModalService,
    private OrderService: OrderService
  ) {}

  ngOnInit(): void {
    const loader = document.getElementById('loader-container');
    loader!.style.display = 'flex';

    this.subscriptions.add(
      this.eventsService.getEvents().subscribe((response: any) => {
        this.allEvents = response.data;
        this.allEvents = this.allEvents.filter((event: any) => event.isVisible !== false && event.actions?.length > 0);
        loader!.style.display = 'none';
        this.areEventsLoaded = true;
      })
    );

    this.orderSub = this.OrderService.order$.subscribe(order => {
      if(order?.items && order.items.length > 0) {
        this.currentOrderEventId = order.items[0].eventId;
      }
    });

    if (environment.configId === "1") {
      this.membershipCountFormatted = this.formatDigits(Array(this.fixedDigitCount).fill('0').join(''));

      this.subscriptions.add(
        this.membershipService.getMembershipCountService().subscribe(response => {
          const membershipCount = response.data;
          this.membershipDataLoaded = true;
          this.animateCountUp(membershipCount, 1500);
        })
      );

      this.subscriptions.add(
        this.mapService.isMapOpen$.subscribe(isOpen => {
          this.isMapOpen = isOpen;
        })
      );
    }

    if(sessionStorage.getItem('openModal') === `HomepageModal`) {
      this.modalService.openModal(ModalNames.HomepageModal);
      let selectedEventFromStorage = localStorage.getItem('selectedEvent');
      this.selectedEvent = selectedEventFromStorage ? JSON.parse(selectedEventFromStorage) : null;
      sessionStorage.removeItem('openModal');
    }
  }

  private animateCountUp(target: number, duration: number): void {
    const startTime = performance.now();

    const update = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const currentValue = Math.floor(progress * target);
      const formattedValue = currentValue.toString().padStart(this.fixedDigitCount, '0');
      this.membershipCountFormatted = this.formatDigits(formattedValue);

      if (progress < 1) {
        requestAnimationFrame(update);
      } else {
        this.membershipCountFormatted = this.formatDigits(
          target.toString().padStart(this.fixedDigitCount, '0')
        );
      }
    };

    requestAnimationFrame(update);
  }

  private formatDigits(value: string): Digit[] {
    let encounteredNonZero = false;
    const digits = value.split('').map(digit => {
      if (!encounteredNonZero && digit === '0') {
        return { digit, leading: true };
      } else {
        if (digit !== '0') {
          encounteredNonZero = true;
        }
        return { digit, leading: false };
      }
    });

    if (!encounteredNonZero && digits.length > 0) {
      digits[digits.length - 1].leading = false;
    }
    return digits;
  }

  openMapOfMembersPerCountie() {
    this.mapService.toggleMap(true);
  }

  selectedEventEmit($event: any) {
    this.parent = "events-list";
    this.selectedEvent = $event;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler() {
    this.mapService.toggleMap(false);
  }

  @HostListener('document:mousedown', ['$event'])
  onGlobalClick(event: MouseEvent) {
    const target = (event.target as Element).id;
    if (target === 'map-container') {
      this.mapService.toggleMap(false);
    }
  }
}
